import { Anchor, Box, Button, Title, useMantineColorScheme } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import classes from "../../../UserProfile.module.css";
import { DeleteAccountConfirmModal } from "./DeleteAccountConfirmModal";
import { DeleteAccountModal } from "./DeleteAccountModal";

import { TextSerif } from "@/components/elements";
import { paths } from "@/config/paths";
import { useDeleteProfile } from "@/services/profile/delete-account-request";

import type { DeleteAccountParams } from "@/services/profile/delete-account-request";

export const DeleteAccountCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openedFirst, { open: openFirst, close: closeFirst }] = useDisclosure();
  const [openedConfirm, { open: openConfirm, close: closeConfirm }] = useDisclosure();
  const [deleteParams, setDeleteParams] = useState<DeleteAccountParams | undefined>();

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  /* APIs */
  const { mutate, isPending } = useDeleteProfile({
    mutationConfig: {
      onSuccess: () => {
        // On successful account deletion, clear cache and redirect to login page
        closeConfirm();
        setDeleteParams(undefined);
        queryClient.clear();
        navigate(paths.auth.login.getHref(), { replace: true });
      },
    },
  });

  /* Handlers */
  const handleFirstModalConfirm = (params?: DeleteAccountParams) => {
    setDeleteParams(params as DeleteAccountParams | undefined);
    closeFirst();
    openConfirm();
  };

  const handleFinalConfirm = () => {
    mutate(deleteParams);
  };

  return (
    <>
      <Box p="sm">
        {/* Header */}
        <Title order={3} fw="normal" fz="1.25em" mb="md">
          {t("profile.delete_account.title")}
        </Title>

        {/* Content */}
        <Box>
          <TextSerif c="dimmed" size="sm" lh="xs">
            {t("profile.delete_account.description")}{" "}
            <Anchor
              component="a"
              href="mailto:support@carburate.com"
              c={isDark ? "var(--mantine-color-primary-3)" : "primary"}
              fw={600}
              tt="uppercase"
              style={{ cursor: "pointer" }}
              aria-label={t("profile.delete_account.contact_support")}
            >
              {t("profile.delete_account.contact_support")}
            </Anchor>
          </TextSerif>
          <TextSerif c="dimmed" size="sm">
            {t("profile.delete_account.confirmation")}
          </TextSerif>
        </Box>

        {/* Actions */}
        <Button
          mt="lg"
          color="red"
          variant="transparent"
          w={{ base: "100%", sm: 300 }}
          maw="100%"
          className={classes.buttonDeleteAccount}
          onClick={openFirst}
          aria-label={t("profile.delete_account.cancel_account")}
          disabled={isPending}
        >
          {t("profile.delete_account.cancel_account")}
        </Button>
      </Box>

      <DeleteAccountModal
        isOpen={openedFirst}
        onClose={closeFirst}
        onConfirm={handleFirstModalConfirm}
        isPending={false}
      />

      <DeleteAccountConfirmModal
        isOpen={openedConfirm}
        closeConfirm={closeConfirm}
        onConfirm={handleFinalConfirm}
        isPending={isPending}
      />
    </>
  );
};
